// src/hooks/trading/useQuickTradeForm.js
import { useState, useEffect, useMemo } from "react";

// Constants for localStorage keys and magic strings
const LS_TRADE_TYPE_KEY = "quickTrade_tradeType";
const LS_SELECTED_WALLET_KEY = "quickTrade_selectedWallet";
const LS_AMOUNT_KEY = "quickTrade_amount";
const LS_SLIPPAGE_KEY = "quickTrade_slippage";
const ODIN_MAIN_WALLET_ID = "ODIN_MAIN_WALLET";

/**
 * Custom hook to manage Quick Trade form state (buy/sell, wallet, amount, slippage)
 * and local storage persistence.
 * @param {number | null} maxAmount - The maximum sellable amount for the token (only relevant for 'sell' tradeType).
 * @param {Array} walletList - List of all available wallets.
 * @param {Array} walletGroups - List of all available wallet groups.
 * @param {Object} mainWallet - The main wallet object.
 * @param {Array} favoriteWallets - Memoized list of favorite wallets.
 * @returns {Object} An object containing form state, handlers, and validation.
 */
export const useQuickTradeForm = (maxAmount, walletList, walletGroups, mainWallet, favoriteWallets) => {
    const [tradeType, setTradeType] = useState("buy"); // 'buy' or 'sell'
    const [selectedWalletOrGroupId, setSelectedWalletOrGroupId] = useState("");
    const [amount, setAmount] = useState("");
    const [slippage, setSlippage] = useState("0");

    // --- Effects ---
    // Load cached values from localStorage on component mount and initialize wallet selection
    useEffect(() => {
        const cachedTradeType = localStorage.getItem(LS_TRADE_TYPE_KEY);
        const cachedWallet = localStorage.getItem(LS_SELECTED_WALLET_KEY);
        const cachedAmount = localStorage.getItem(LS_AMOUNT_KEY);
        const cachedSlippage = localStorage.getItem(LS_SLIPPAGE_KEY);

        if (cachedTradeType) {
            setTradeType(cachedTradeType);
        }
        if (cachedSlippage !== null && !isNaN(Number(cachedSlippage))) {
            setSlippage(cachedSlippage);
        }

        // Determine initial wallet/group selection
        let initialSelection = "";
        const isValidCachedWallet = walletList.some((w) => w._id === cachedWallet);
        const isValidCachedGroup = walletGroups.some((g) => g._id === cachedWallet);

        if (cachedWallet && (isValidCachedWallet || isValidCachedGroup || (cachedWallet === ODIN_MAIN_WALLET_ID && mainWallet))) {
            initialSelection = cachedWallet;
        } else if (favoriteWallets.length > 0) {
            initialSelection = favoriteWallets[0]._id;
        } else if (walletGroups.length > 0) {
            initialSelection = walletGroups[0]._id;
        }

        setSelectedWalletOrGroupId(initialSelection);
        if (initialSelection && initialSelection !== cachedWallet) {
            localStorage.setItem(LS_SELECTED_WALLET_KEY, initialSelection);
        }

        // Set amount based on trade type and cached value
        if (cachedAmount) {
            setAmount(cachedAmount);
        } else if (tradeType === "sell" && maxAmount !== null) {
            setAmount(String(maxAmount)); // Set to maxAmount if selling and no cached amount
        }
    }, [walletList, walletGroups, favoriteWallets, mainWallet, maxAmount, tradeType]); // Re-run if wallets/maxAmount/tradeType changes

    // Update amount if maxAmount changes and tradeType is 'sell'
    useEffect(() => {
        if (tradeType === "sell" && maxAmount !== null) {
            setAmount(String(maxAmount));
        }
    }, [tradeType, maxAmount]);

    // --- Handlers ---
    const handleTradeTypeChange = (newType) => {
        setTradeType(newType);
        localStorage.setItem(LS_TRADE_TYPE_KEY, newType);
        // Reset amount if switching to sell and maxAmount is available
        if (newType === "sell" && maxAmount !== null) {
            setAmount(String(maxAmount));
        } else if (newType === "buy") {
            setAmount(""); // Clear amount when switching to buy
        }
    };

    const handleWalletChange = (e) => {
        const newValue = e.target.value;
        setSelectedWalletOrGroupId(newValue);
        localStorage.setItem(LS_SELECTED_WALLET_KEY, newValue);
    };

    const handleAmountChange = (e) => {
        const { value } = e.target;
        if (/^\d*\.?\d*$/.test(value)) {
            setAmount(value);
            localStorage.setItem(LS_AMOUNT_KEY, value);
        }
    };

    const handleSlippageChange = (e) => {
        const { value } = e.target;
        if (/^\d*\.?\d*$/.test(value)) {
            const numValue = parseFloat(value);
            if (value === "" || (numValue >= 0 && numValue <= 100)) {
                setSlippage(value);
                localStorage.setItem(LS_SLIPPAGE_KEY, value);
            }
        }
    };

    // --- Derived State for Validation ---
    const isValidAmount = useMemo(() => {
        const num = parseFloat(amount);
        if (isNaN(num) || num <= 0) return false;
        if (tradeType === "sell" && maxAmount !== null && num > maxAmount) return false;
        return true;
    }, [amount, tradeType, maxAmount]);

    const isValidSlippage = useMemo(() => {
        const num = parseFloat(slippage);
        return !isNaN(num) && num >= 0 && num <= 100;
    }, [slippage]);

    return {
        tradeType,
        selectedWalletOrGroupId,
        amount,
        slippage,
        handleTradeTypeChange,
        handleWalletChange,
        handleAmountChange,
        handleSlippageChange,
        isValidAmount,
        isValidSlippage,
        ODIN_MAIN_WALLET_ID,
    };
};
